// src/components/BusinessExpensesTable.tsx
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  TableFooter,
  Input,
    Paper, Button,
    Box,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    Typography
} from '@mui/material';
import styles from './BusinessExpensesTable.module.css';
import { BalanceSheet } from '../types/StockhornTypes';
import {Cookies}  from '../js/localStorageCode';
import AddBalanceSheetItems from './AddBalanceSheetItems';
import {AddCommas} from './Utilities';
const environment = document.location.hostname.match(/meekmeek/) ? 'PROD' : document.location.hostname.match(/vercel/) ? 'PREVIEW': 'DEV';
    document.title = environment+":Balance Sheet";
const initialExpenses: BalanceSheet[] = [
  { id: 1,  name: '', description: '', amount:0 },
];

const parseBalanceSheet = (value: string | null): BalanceSheet[] | null => {
  if (!value) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(value);
    return Array.isArray(parsedValue) ? parsedValue : null;
  } catch {
    return null;
  }
};

const downloadFile = (contents: string, fileName: string, type: string) => {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const escapeCsvValue = (value: string | number): string =>
  `"${String(value).replace(/"/g, '""')}"`;

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[character] || character));

const BalanceSheetTable: React.FC = () => {
  const cookies = new Cookies();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [localExpenses] = useState<BalanceSheet[] | null>(() => {
    const saved = cookies.get("BalanceSheet");
    return saved ? parseBalanceSheet(saved) : null;
  });
  const [sharedExpenses] = useState<BalanceSheet[] | null>(() =>
    parseBalanceSheet(new URLSearchParams(window.location.search).get('balanceSheet'))
  );
  const [expenses, setExpenses] = useState<BalanceSheet[]>(
    sharedExpenses || localExpenses || initialExpenses
  );

  useEffect(() => {
    if (sharedExpenses) {
      setOverrideDialogOpen(true);
    }
  }, [sharedExpenses, localExpenses]);

  const handleOverride = () => {
    if (sharedExpenses) {
      cookies.set("BalanceSheet", JSON.stringify(sharedExpenses), {expires: 365});
      setExpenses(sharedExpenses);
    }
    setOverrideDialogOpen(false);
  };

  const handleKeepLocalStorage = () => {
    if (sharedExpenses) {
      setExpenses(sharedExpenses);
    }
    setOverrideDialogOpen(false);
  };

  const handleCreateShareLink = () => {
    const newShareUrl = new URL(window.location.origin + window.location.pathname);
    newShareUrl.searchParams.set('balanceSheet', JSON.stringify(expenses));
    setShareUrl(newShareUrl.toString());
  };

  const handleCopyLink = async () => {
    if (!shareUrl) {
      return;
    }
    await navigator.clipboard.writeText(shareUrl);
    alert('Balance sheet link copied');
  };

  const handleDownloadJson = () => {
    downloadFile(JSON.stringify(expenses, null, 2), 'balance-sheet.json', 'application/json');
  };

  const handleDownloadCsv = () => {
    const header = ['Name', 'Details/Description', 'Amount'];
    const rows = expenses.map((expense) => [
      expense.name,
      expense.description || expense.name,
      expense.amount,
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map(escapeCsvValue).join(','))
      .join('\n');
    downloadFile(csv, 'balance-sheet.csv', 'text/csv');
  };

  const handlePrintSummary = () => {
    const printWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (!printWindow) {
      return;
    }

    const rows = expenses.map((expense) => `
      <tr>
        <td>${escapeHtml(expense.name)}</td>
        <td>${escapeHtml(expense.description || expense.name)}</td>
        <td>${escapeHtml(String(expense.amount))}</td>
      </tr>`).join('');
    printWindow.document.write(`
      <html>
        <head>
          <title>Balance Sheet Summary</title>
          <style>
            @page { size: auto; margin: 0.6in; }
            body { font-family: Arial, sans-serif; color: #111; }
            table { width: 100%; border-collapse: collapse; }
            th { text-align: left; }
            td, th { padding: 6px; border: 1px solid #999; }
            tfoot th:last-child { text-align: right; }
          </style>
        </head>
        <body>
          <h1>Balance Sheet Summary</h1>
          <table border="1" cellpadding="6" cellspacing="0">
            <thead><tr><th>Name</th><th>Details/Description</th><th>Amount</th></tr></thead>
            <tbody>${rows}</tbody>
            <tfoot><tr><th colspan="2" align="right">Total</th><th>${escapeHtml(String(totalAmount))}</th></tr></tfoot>
          </table>
        </body>
      </html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };


  const handleDescriptionChange = (id: number, description: string) => {
    setExpenses((prev) =>
      prev.map((expense) =>
        expense.id === id ? { ...expense, description } : expense
      )
    );
  };

    const handleNameChange = (id: number, name: string) => {
    setExpenses((prev) =>
      prev.map((expense) =>
        expense.id === id ? { ...expense, name } : expense
      )
    );
  };

  

    const handleAmountChange = (id: number, amount: number) => {
    setExpenses((prev) =>
      prev.map((expense) =>
        expense.id === id ? { ...expense, amount } : expense
      )
    );
  };

  const handleSave = (id: number) => {
    const expense = expenses.find((e) => e.id === id);
    if (expense) {
      //localStorage.setItem('businessExpenses', JSON.stringify(expenses));
      cookies.set("BalanceSheet", JSON.stringify(expenses), {expires: 365});
      alert(`Saved: Name: ${expense.name}, Description: ${expense.description}, Amount: ${expense.amount})`);
    }
  };

  const handleDelete = (id: number) => {

    const newValue = expenses.filter((expense) => expense.id !== id);
    const oldValue = expenses.find((expense) => expense.id === id);
    const confirmDelete: boolean = window.confirm(`Are you sure you want to delete "${oldValue?.name}"?`);
    if(confirmDelete){
    setExpenses(newValue);
    cookies.set("BalanceSheet", JSON.stringify(newValue), {expires: 365});
    if(oldValue?.name){
      alert(`Deleted row: Name: ${oldValue.name}`);
    }
  }
  };

  const handleAddExpense = (name: string) => {
    if (expenses.some((e) => e.name.toLowerCase() === name.toLowerCase())) {
      alert('Expense name already exists');
      return;
    }
    const newId = Math.max(...expenses.map((e) => e.id), 0) + 1;
    setExpenses((prev) => [
      ...prev,
      { id: newId, name, description: '', amount: 0 },
    ]);
  };

   let totalAmount: number = 0;
    expenses.forEach(expense=> {
      totalAmount += (expense.amount? expense.amount : 0);
    });

  return (
            <Box className="MyTable" p={3}>
            <Paper elevation={0} sx={{ mb: 4, p: 0 }}>
      <Table className={styles.table} stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell className={styles.nameCell}>Name</TableCell>
            <TableCell className={styles.descriptionCell}>Details/Description</TableCell>
            <TableCell className={styles.descriptionAmount}>Amount</TableCell>
            <TableCell className={styles.updateCell}>SAVE</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {expenses.map((expense) => (
            <TableRow key={expense.id}>
              <TableCell>
                <TextField
                  value={expense.name}
                  onChange={(e) => handleNameChange(expense.id, e.target.value)}
                  className={styles.descriptionInput}
                  placeholder="Enter Name"
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <TextField
                  value={expense.description}
                  onChange={(e) => handleDescriptionChange(expense.id, e.target.value)}
                  className={styles.descriptionInput}
                  placeholder={expense.name || 'Enter details'}
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <Input
                  type='number'
                  inputProps={{step: 'any'}}
                  value={(expense.amount)}
                  onChange={(e) => handleAmountChange(expense.id, Number(e.target.value))}
                  className={styles.descriptionAmount}
                  placeholder="Enter amount"
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleSave(expense.id)}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  className={styles.deleteButton}
                  onClick={() => handleDelete(expense.id)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
           <TableRow className={styles.totalRow}>
            <TableCell colSpan={2}>Total</TableCell>
            <TableCell>${AddCommas(totalAmount)}</TableCell>
            <TableCell />
          </TableRow>
          <TableRow className={styles.footerRow}>
            <TableCell colSpan={4} align="center">
              <Button
                variant="contained"
                className={styles.addButton}
                onClick={() => setAddModalOpen(true)}
              >
                Add Row
              </Button>
              <Button
                variant="outlined"
                className={styles.addButton}
                onClick={() => setSummaryDialogOpen(true)}
              >
                Show Summary
              </Button>
              <Button
                variant="outlined"
                className={styles.addButton}
                onClick={handleCreateShareLink}
              >
                Create Share Link
              </Button>
              {shareUrl && (
                <Button
                  variant="text"
                  className={styles.addButton}
                  onClick={handleCopyLink}
                >
                  Copy Link
                </Button>
              )}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
      <AddBalanceSheetItems
        open={addModalOpen}
        onSubmit={handleAddExpense}
        onClose={() => setAddModalOpen(false)}
      />
      <Dialog
        open={summaryDialogOpen}
        onClose={() => setSummaryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Balance Sheet Summary</DialogTitle>
        <DialogContent>
          {expenses.map((expense) => (
            <Box key={expense.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1 }}>
              <Typography>{expense.name}</Typography>
              <Typography>{expense.description || expense.name}</Typography>
              <Typography>${AddCommas(expense.amount)}</Typography>
            </Box>
          ))}
          <Typography sx={{ borderTop: '1px solid', mt: 1, pt: 1, textAlign: 'right' }}>
            <strong>Total: ${AddCommas(totalAmount)}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadCsv}>Create CSV</Button>
          <Button onClick={handleDownloadJson}>Create JSON</Button>
          <Button onClick={handlePrintSummary}>Print / Save PDF</Button>
          <Button onClick={() => setSummaryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {localExpenses
            ? 'This link contains a balance sheet that will be shown. Do you also want to override your locally saved balance sheet with these new values?'
            : 'This link contains a balance sheet that will be shown. Do you want to save it locally?'}
        </DialogTitle>
        <DialogActions>
          <Button onClick={handleKeepLocalStorage}>No</Button>
          <Button onClick={handleOverride} variant="contained">Yes</Button>
        </DialogActions>
      </Dialog>
    </Paper>
    </Box>
  );
};

export default BalanceSheetTable;